import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { CopyButton } from "@/core/components/ui/copy-button";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import type { SavingsVaultDto } from "@/features/savings/api/dto";
import {
  SAVINGS_VAULT_STATUSES,
  type SavingsVaultStatus,
} from "@/features/savings/api/enums";
import { SavingsVaultRowActions } from "@/features/savings/components/vaults/savings-vault-row-actions";
import { AmountMinor } from "@/features/shared/components/amount-minor";

/** Ligne de la liste : `DataTableClar` exige un `id` requis. */
export type SavingsVaultRow = SavingsVaultDto & { id: string };

const STATUS_VARIANTS: Record<SavingsVaultStatus, "success" | "secondary"> = {
  OPEN: "success",
  CLOSED: "secondary",
};

const statusLabel = (status: string) =>
  t(`savings.enums.vaultStatus.${status}`);

/**
 * Colonnes de la liste des coffres (`POST /SavingsVaults/all`).
 * `SavingsVaultFilter` (Postman v3.1) : `status`, `ownerKeycloakUserId`
 * uniquement — les autres colonnes n'ont pas de champ API, d'où `denied`.
 * Montants et identifiants en `NoTranslate` (règle notranslate).
 */
export const savingsVaultsColumns: ColumnsClarFactory<SavingsVaultRow> = (
  navigate
) => [
  {
    id: "name",
    accessorKey: "name",
    header: t("savings.vaults.columns.name"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-medium">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    // Pas dans SavingsVaultFilter (Postman : status / ownerKeycloakUserId).
    meta: { type: "denied" },
  },
  {
    id: "currency",
    accessorKey: "currency",
    header: t("savings.vaults.columns.currency"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "balanceMinor",
    accessorKey: "balanceMinor",
    header: t("savings.vaults.columns.balance"),
    cell: ({ row }) => (
      <AmountMinor
        amountMinor={row.original.balanceMinor}
        currency={row.original.currency}
        className="font-medium"
      />
    ),
    meta: { type: "denied" },
  },
  {
    id: "goalProgressPercent",
    accessorKey: "goalProgressPercent",
    header: t("savings.vaults.columns.goalProgress"),
    cell: ({ getValue }) => {
      const progress = getValue() as number | null | undefined;
      return progress != null ? (
        <NoTranslate as="span">{`${progress} %`}</NoTranslate>
      ) : (
        "—"
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("savings.vaults.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as SavingsVaultStatus | undefined;
      return status ? (
        <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>
          {statusLabel(status)}
        </Badge>
      ) : (
        "—"
      );
    },
    meta: {
      type: "select",
      options: SAVINGS_VAULT_STATUSES.map((value) => ({
        value,
        label: statusLabel(value),
      })),
    },
  },
  {
    id: "lockedUntil",
    accessorKey: "lockedUntil",
    header: t("savings.vaults.columns.lockedUntil"),
    cell: ({ row }) =>
      row.original.isLocked && row.original.lockedUntil ? (
        <NoTranslate as="span" className="text-xs">
          {formatDateWithFallback(row.original.lockedUntil, "—")}
        </NoTranslate>
      ) : (
        "—"
      ),
    meta: { type: "denied" },
  },
  {
    id: "ownerKeycloakUserId",
    accessorKey: "ownerKeycloakUserId",
    header: t("savings.vaults.columns.ownerKeycloakUserId"),
    cell: ({ getValue }) => {
      const owner = (getValue() as string | undefined)?.trim();
      if (!owner) return "—";
      return (
        <span className="inline-flex items-center gap-1">
          <NoTranslate as="span" className="font-mono text-xs">
            {owner}
          </NoTranslate>
          <CopyButton value={owner} />
        </span>
      );
    },
    // Filtre back-office (ignoré côté client, §6.10).
    meta: { type: "text" },
  },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: t("savings.vaults.columns.createdAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <SavingsVaultRowActions vault={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
